import React from 'react'
import Select, { components } from 'react-select'
import { LogsFilterSortedDown, LogsFilterSortedUp, NavBarSortedDown, NavBarSortedUp } from './ProjectIcons';
import { activityLogStyle, defaultStyle, getDefaultStyleWithDarkMode, languageSelectStyle, timezoneStyle } from './componentstyles/selectinputstyles';
import { useTheme } from '../../context/ThemeContext';

export type SelectVariant = 'default' | 'language' | 'timezone' | 'activityLog';

type SelectInputProps = {
    options: Array<any>,
    className: string,
    defaultValue?: object,
    value?: object,
    isDisabled: boolean,
    isLoading: boolean,
    isClearable: boolean,
    isSearchable: boolean,
    isMulti: boolean,
    name: string,
    onFocus?: () => void,
    onChange: Function,
    onMenuScrollToBottom?: any,
    handleInputChange?: any,
    variant?: SelectVariant,
    languageSelect?: boolean,// @deprecated Use variant="language" instead
    timezoneSelect?: boolean, // @deprecated Use variant="timezone" instead
    components?: any,
    onMenuOpen?: () => void,
    onMenuClose?: () => void,
    keepOptionsOrder?: boolean,
}

const CustomOption = (props: any) => {
    const { data, innerRef, innerProps } = props;
    return (
        <div ref={innerRef} {...innerProps} className='p-3 flex flex-col hover:bg-light-blue dark:hover:bg-neutral-gray-700 w-full whitespace-nowrap'>
            <div className='text-off-black dark:text-white'>{data.label}</div>
            <div className='text-secondary-gray dark:text-gray-lines'>{data.subtitle}</div>
        </div>
    );
};

const CustomSingleValue = (props: any) => {
    const { data } = props;
    const { darkMode } = useTheme();

    return (
        <components.SingleValue {...props} className={darkMode ? 'dark:text-white' : undefined}>
            <span>{data.label}</span>
        </components.SingleValue>
    );
};

const NavDropdownIndicator = (props: any) => {
    const { selectProps } = props;
    const { darkMode } = useTheme();
    const className = darkMode ? 'dark:text-white' : undefined;

    return (
        <components.DropdownIndicator {...props} className={className}>
            {selectProps.menuIsOpen ? <NavBarSortedUp /> : <NavBarSortedDown />}
        </components.DropdownIndicator>
    );
};

const DefaultDropdownIndicator = (props: any) => {
    const { selectProps } = props;
    const { darkMode } = useTheme();
    const className = darkMode ? 'dark:text-white' : undefined;

    return (
        <components.DropdownIndicator {...props} className={className}>
            {selectProps.menuIsOpen ? <LogsFilterSortedUp /> : <LogsFilterSortedDown />}
        </components.DropdownIndicator>
    );
};

const SelectInput = ({
    options,
    className,
    defaultValue,
    value,
    isDisabled,
    isLoading,
    isClearable,
    isSearchable,
    isMulti,
    name,
    onFocus,
    onChange,
    onMenuScrollToBottom,
    handleInputChange,
    variant = 'default',
    languageSelect,
    timezoneSelect,
    keepOptionsOrder = false
}: SelectInputProps) => {
    // For backward compatibility
    if (languageSelect) variant = 'language';
    if (timezoneSelect) variant = 'timezone';

    // Use the ThemeContext to get the current theme
    const { darkMode: isDarkMode } = useTheme();

    // Get styles based on variant and dark mode
    const getStylesForVariant = () => {
        // Create custom style functions that adapt based on dark mode
        const createDarkModeStyleObj = (styleObj: any) => {
            return Object.entries(styleObj).reduce((acc: any, [key, styleFn]: [string, any]) => {
                acc[key] = (provided: any, state: any) => {
                    const baseStyle = styleFn(provided, state);

                    // If dark mode is enabled and the style has dark mode properties
                    if (isDarkMode && baseStyle['@media (prefers-color-scheme: dark)']) {
                        // Merge the base style with the dark mode overrides
                        return {
                            ...baseStyle,
                            ...baseStyle['@media (prefers-color-scheme: dark)'],
                            '@media (prefers-color-scheme: dark)': undefined, // Remove the media query
                        };
                    }

                    // Ensure light mode explicitly gets its styles (no media queries)
                    return {
                        ...baseStyle,
                        '@media (prefers-color-scheme: dark)': undefined, // Remove the media query in light mode too
                    };
                };
                return acc;
            }, {});
        };

        // Apply the correct style based on variant
        switch (variant) {
            case 'timezone':
                return createDarkModeStyleObj(timezoneStyle);
            case 'language':
                return createDarkModeStyleObj(languageSelectStyle);
            case 'activityLog':
                return createDarkModeStyleObj(activityLogStyle);
            default:
                return getDefaultStyleWithDarkMode(isDarkMode);
        }
    };

    const styles = getStylesForVariant();

    // Function to reorder options
    const reorderOptions = (options: any[], selectedValue: any) => {
        if (!selectedValue || keepOptionsOrder) return options;
        const selectedIndex = options.findIndex(option => option.label === selectedValue.label);
        if (selectedIndex === -1) return options;
        const [selectedOption] = options.splice(selectedIndex, 1);
        return [selectedOption, ...options];
    };

    // Reorder options based on the selected value
    const reorderedOptions = reorderOptions([...options], value);

    // Define custom components outside the getComponentsForVariant function
    const DefaultOption = (props: any) => {
        const { children, ...rest } = props;
        const { darkMode } = useTheme();
        return (
            <components.Option {...rest} className={darkMode ? 'dark:text-white' : undefined}>
                {children}
            </components.Option>
        );
    };

    const DefaultSingleValue = (props: any) => {
        const { children, ...rest } = props;
        const { darkMode } = useTheme();
        return (
            <components.SingleValue {...rest} className={darkMode ? 'dark:text-white' : undefined}>
                {children}
            </components.SingleValue>
        );
    };

    // Get components based on variant
    const getComponentsForVariant = () => {
        const baseComponents: any = {};

        // Add DropdownIndicator for specific variants
        if (['language', 'timezone'].includes(variant)) {
            baseComponents.DropdownIndicator = NavDropdownIndicator;
        }

        // Add CustomOption and CustomSingleValue for specific variants
        if (['timezone', 'activityLog'].includes(variant)) {
            baseComponents.Option = CustomOption;
            baseComponents.SingleValue = CustomSingleValue;
        } else {
            // For variants that don't have custom option components, we still need to add dark mode support
            baseComponents.Option = DefaultOption;
            baseComponents.SingleValue = DefaultSingleValue;
        }

        if (['activityLog'].includes(variant)) {
            baseComponents.DropdownIndicator = DefaultDropdownIndicator;
        }

        return baseComponents;
    };

    return (
        <Select
            placeholder=""
            options={reorderedOptions} // Use reordered options here
            className={className}
            defaultValue={defaultValue}
            value={value}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isClearable={isClearable}
            isSearchable={isSearchable}
            isMulti={isMulti}
            name={name}
            onFocus={onFocus}
            onChange={(e) => onChange(e)}
            onMenuScrollToBottom={onMenuScrollToBottom}
            onInputChange={handleInputChange}
            styles={styles}
            components={getComponentsForVariant()}
            filterOption={(option: any, input) => {
                if (!input) return true;
                const lowerCaseInput = input.toLowerCase().trim();
                const searchableFields = [
                    option.label.toLowerCase(),
                    ...(option.data.searchTerms || [])
                ];

                return searchableFields.some(field =>
                    field.includes(lowerCaseInput) ||
                    lowerCaseInput.split(" ").every((word: string) => field.includes(word))
                );
            }}
        />
    );
};

export default SelectInput;